import {clsx} from '@gfiber/typesmith';
import type React from 'react';
import {useRef} from 'react';
import {Ripple, type RippleRef} from '../../Ripple/Ripple';
import styles from './ListItem.module.scss';

interface ListItemProps {
  className?: string;
  disabled?: boolean;
  graphic?: React.ReactNode;
  meta?: React.ReactNode;
  onClick?: (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => void;
  round?: boolean;
  secondaryText?: string;
  selected?: boolean;
  children: React.ReactNode;
  testId?: string;
  tag?: React.ElementType;
  [key: string]: unknown;
}

export const ListItem: React.FC<ListItemProps> = ({
  className,
  disabled,
  graphic,
  meta,
  onClick,
  round,
  secondaryText,
  selected,
  children,
  testId,
  tag: Tag = 'li',
  ...rest
}) => {
  const rippleRef = useRef<RippleRef>(null);
  const isClickable = (!!onClick || Tag !== 'li') && !disabled;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;

    //Important: Trigger visual ripple via the exposed ref method
    rippleRef.current?.trigger(event);

    //Important: Trigger functional callback
    onClick?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!isClickable) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      // Trigger the ripple from the center
      rippleRef.current?.trigger(event);

      onClick?.(event);
    }
  };

  return (
    <Tag
      {...rest}
      aria-current={selected ? 'true' : undefined}
      aria-disabled={disabled}
      className={clsx(
        styles.listItem,
        {
          [styles.clickable]: isClickable,
          [styles.disabled]: disabled,
          [styles.round]: round,
          [styles.selected]: selected,
        },
        className,
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={
        (rest.role as React.AriaRole) ||
        (Tag === 'li' ? (onClick ? 'button' : 'listitem') : undefined)
      }
      tabIndex={isClickable ? 0 : undefined}
      data-testid={testId}
    >
      <Ripple ref={rippleRef} />

      {graphic && <span className={styles.graphic}>{graphic}</span>}

      <span className={styles.textContainer}>
        <span className={styles.primaryText}>{children}</span>
        {secondaryText && (
          <span className={styles.secondaryText}>{secondaryText}</span>
        )}
      </span>

      {meta && <span className={styles.meta}>{meta}</span>}
    </Tag>
  );
};
